import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  Phone, MapPin, Users, Fuel, Cog, Gauge, Snowflake, Share2,
  ChevronLeft, ChevronRight, CheckCircle2, ShieldCheck, IndianRupee,
  Calendar, Sparkles, MessageCircle, Car as CarIcon, Tag, FileText, Award, CalendarCheck,
  Building2, ArrowRight
} from 'lucide-react';
import ShareSheet from '../../components/ui/ShareSheet';
import BookVehicleModal from '../../components/public/BookVehicleModal';
import ListingDisclaimer from '../../components/legal/ListingDisclaimer';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:7000/api';
const NAVY = '#07142A';
const YELLOW = '#FFD400';

const inr = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);
const tc = (s = '') => String(s).replace(/\b\w/g, (c) => c.toUpperCase());
const digits = (p = '') => String(p).replace(/\D/g, '');
const waNumber = (p) => { const d = digits(p); return d.length === 10 ? `91${d}` : d; };
const ordinal = (n) => { const s = ['th', 'st', 'nd', 'rd'], v = n % 100; return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`; };
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }); } catch { return ''; } };
const SHARE_LOAN_LABELS = {
  none: 'No loan taken',
  closed: 'Closed — NOC available',
  active: 'Running — NOC pending',
};

/* Reveal-on-scroll wrapper */
const Reveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setShow(true); io.disconnect(); } },
      { threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: show ? 1 : 0,
        transform: show ? 'translateY(0)' : 'translateY(28px)',
        transition: `opacity .7s ease ${delay}ms, transform .7s cubic-bezier(.16,1,.3,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

/* Cinematic image gallery */
const Gallery = ({ images = [], status, location }) => {
  const [i, setI] = useState(0);
  const has = images.length > 0;
  const go = useCallback((n) => setI((p) => (p + n + images.length) % images.length), [images.length]);

  useEffect(() => {
    if (images.length < 2) return;
    const t = setInterval(() => setI((p) => (p + 1) % images.length), 4500);
    return () => clearInterval(t);
  }, [images.length]);

  return (
    <div className="relative w-full overflow-hidden rounded-3xl shadow-2xl" style={{ aspectRatio: '16/10', background: NAVY }}>
      {has ? images.map((src, idx) => (
        <img
          key={idx} src={src} alt="" loading={idx === 0 ? 'eager' : 'lazy'}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: idx === i ? 1 : 0, transition: 'opacity 1s ease', transform: idx === i ? 'scale(1)' : 'scale(1.06)', transitionProperty: 'opacity, transform' }}
        />
      )) : (
        <div className="absolute inset-0 flex items-center justify-center text-white/40"><CarIcon size={80} /></div>
      )}

      {/* top gradient + badge */}
      <div className="absolute inset-x-0 top-0 h-24" style={{ background: 'linear-gradient(to bottom, rgba(7,20,42,.55), transparent)' }} />
      <div className="absolute top-4 left-4">
        <span className="px-3 py-1.5 rounded-full text-xs font-bold shadow-lg flex items-center gap-1.5"
          style={{ background: status?.color || '#16a34a', color: '#fff' }}>
          {status?.pulse && <span className="w-2 h-2 rounded-full bg-white/90 animate-pulse" />} {status?.label || 'Available'}
        </span>
      </div>

      {/* Availability LOCATION pill — makes it clear where this vehicle is based */}
      {location && (
        <div className="absolute top-4 right-4 max-w-[58%]">
          <span className="px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg flex items-center gap-1.5"
            style={{ background: 'rgba(7,20,42,.65)', color: '#fff', backdropFilter: 'blur(4px)' }}>
            <MapPin size={13} style={{ color: YELLOW }} /> <span className="truncate">Available in {location}</span>
          </span>
        </div>
      )}

      {images.length > 1 && (
        <>
          <button onClick={() => go(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/35 backdrop-blur text-white flex items-center justify-center hover:bg-black/55 transition"><ChevronLeft /></button>
          <button onClick={() => go(1)} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/35 backdrop-blur text-white flex items-center justify-center hover:bg-black/55 transition"><ChevronRight /></button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {images.map((_, idx) => (
              <button key={idx} onClick={() => setI(idx)}
                className="h-2 rounded-full transition-all"
                style={{ width: idx === i ? 26 : 8, background: idx === i ? YELLOW : 'rgba(255,255,255,.6)' }} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const Spec = ({ icon: Icon, label, value }) => (
  <div className="flex flex-col items-center text-center gap-1 py-3 px-2 rounded-2xl bg-white shadow-sm border border-gray-100">
    <span className="w-10 h-10 rounded-full flex items-center justify-center mb-1" style={{ background: '#EAF6FF', color: '#0369a1' }}><Icon size={20} /></span>
    <span className="text-[15px] font-bold" style={{ color: NAVY }}>{value}</span>
    <span className="text-xs text-gray-500">{label}</span>
  </div>
);

const PriceCard = ({ label, amount, unit, highlight }) => (
  <div className="rounded-2xl p-4 border shadow-sm" style={{ background: highlight ? NAVY : '#fff', borderColor: highlight ? NAVY : '#eef0f3' }}>
    <div className="text-xs font-semibold mb-1" style={{ color: highlight ? YELLOW : '#6b7280' }}>{label}</div>
    <div className="text-2xl font-extrabold leading-none" style={{ color: highlight ? '#fff' : NAVY }}>{inr(amount)}</div>
    <div className="text-xs mt-1" style={{ color: highlight ? 'rgba(255,255,255,.7)' : '#9ca3af' }}>{unit}</div>
  </div>
);

export default function VehicleShare() {
  const { carId } = useParams();
  const [searchParams] = useSearchParams();
  const urlMode = searchParams.get('mode'); // 'buy' | 'rent' | null
  const [car, setCar] = useState(null);
  const [state, setState] = useState('loading'); // loading | ok | error
  const [shareOpen, setShareOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  // Over the owner's plan limit — the vehicle exists, it just isn't listed right now.
  const [notListed, setNotListed] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/cars/public/${carId}`);
        const data = await r.json();
        if (!alive) return;
        if (data.success && data.car) { setCar(data.car); setState('ok'); }
        else { setNotListed(data.code === 'NOT_LISTED'); setState('error'); }
      } catch { if (alive) setState('error'); }
    })();
    return () => { alive = false; };
  }, [carId]);

  useEffect(() => {
    if (car) document.title = `${[car.make, car.model].filter(Boolean).join(' ')} — Wheels4Rent`;
  }, [car]);

  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 rounded-full border-4 border-gray-200 animate-spin" style={{ borderTopColor: YELLOW }} />
      </div>
    );
  }
  if (state === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center text-center px-6">
        <CarIcon size={56} className="text-gray-300 mb-4" />
        <h1 className="text-xl font-bold" style={{ color: NAVY }}>
          {notListed ? 'Vehicle not listed' : 'Vehicle not found'}
        </h1>
        <p className="text-gray-500 mt-1">
          {notListed
            ? 'This vehicle is not currently listed. Please contact the travel agency directly.'
            : 'This vehicle may have been removed or the link is invalid.'}
        </p>
        <a href="https://wheels4rent.in" className="mt-5 px-5 py-2.5 rounded-xl font-semibold text-white" style={{ background: NAVY }}>Go to Wheels4Rent</a>
      </div>
    );
  }

  const owner = car.ownerId || {};
  const ti = owner.travelsInfo || {};
  const agency = ti.name || owner.name || 'Wheels4Rent Partner';
  const logo = ti.logo?.url;
  const phone = ti.phone || owner.phone || '';
  const name = [car.make, car.model].filter(Boolean).join(' ') || 'Vehicle';
  const cityLine = [car.location?.city, car.location?.state].filter(Boolean).join(', ');
  const vd = car.vehicleDetails || {};
  const shareUrl = `https://wheels4rent.in/share/vehicle/${car._id}`;

  const specs = [
    car.seats && { icon: Users, label: 'Seats', value: car.seats },
    car.fuelType && { icon: Fuel, label: 'Fuel', value: tc(car.fuelType) },
    car.transmission && { icon: Cog, label: 'Gearbox', value: tc(car.transmission) },
    (vd.acAvailable || (car.features || []).some((f) => /a\/?c|air ?condition/i.test(f))) && { icon: Snowflake, label: 'AC', value: 'Yes' },
    vd.engineCC && { icon: Gauge, label: 'Engine', value: `${vd.engineCC}cc` },
    car.year && { icon: Calendar, label: 'Year', value: car.year },
  ].filter(Boolean);

  const allPrices = [
    car.pricePerDay && { key: 'daily', label: 'Per Day', amount: car.pricePerDay, unit: 'per day' },
    car.pricePerHour && { key: 'local', label: 'Per Hour', amount: car.pricePerHour, unit: 'per hour' },
    car.pricePerKm && { key: 'km-based', label: 'Per KM', amount: car.pricePerKm, unit: 'per km' },
    car.outstationPricePerKm && { key: 'outstation', label: 'Outstation', amount: car.outstationPricePerKm, unit: 'per km' },
  ].filter(Boolean);

  // Primary rate = highlighted + shown first. We PREFER Per KM: a vehicle that
  // rents per-km leads with Per KM, not Per Day (pricePerDay is required on every
  // car, so it can't be used to tell them apart). Priority runs km -> outstation
  // -> day -> hour, favouring a rate whose service type the owner enabled.
  const typeToKey = { daily: 'daily', rental: 'daily', 'km-based': 'km-based', outstation: 'outstation', local: 'local' };
  const enabledKeys = new Set((Array.isArray(car.serviceTypes) ? car.serviceTypes : []).map((t) => typeToKey[t]).filter(Boolean));
  const RATE_PRIORITY = ['km-based', 'outstation', 'daily', 'local'];
  const hasPrice = (k) => allPrices.some((p) => p.key === k);
  const primaryKey =
    RATE_PRIORITY.find((k) => enabledKeys.has(k) && hasPrice(k)) ||
    RATE_PRIORITY.find((k) => hasPrice(k)) ||
    allPrices[0]?.key;
  const prices = allPrices
    .map((p) => ({ ...p, highlight: p.key === primaryKey }))
    .sort((a, b) => (b.highlight ? 1 : 0) - (a.highlight ? 1 : 0));
  const primaryPrice = prices.find((p) => p.highlight) || prices[0] || null;

  // Purpose: for rent, for sale, or both
  const forSale = !!car.forSale;
  const forRent = car.forRent !== false;
  const sold = !!car.isSold;
  const sd = car.saleDetails || {};
  const showSalePrice = forSale && car.salePrice > 0 && car.salePriceType !== 'contact-for-price';
  const saleTypeLabel = { fixed: 'Fixed Price', negotiable: 'Price Negotiable', 'best-offer': 'Best Offer', 'contact-for-price': 'Contact for Price' }[car.salePriceType] || 'Negotiable';
  const purposeLabel = sold ? 'Sold'
    : urlMode === 'buy' ? 'For Sale'
    : urlMode === 'rent' ? 'For Rent'
    : forRent && forSale ? 'For Rent & Sale'
    : forSale ? 'For Sale' : 'For Rent';

  // Which view leads: explicit ?mode wins, else infer from the car's flags
  const leadBuy = urlMode === 'buy' ? true : urlMode === 'rent' ? false : forSale && !forRent;
  const buyMode = leadBuy && forSale;

  // A link shared with an explicit ?mode shows ONLY that side's details
  // (buy link → hide rental, rent link → hide sale). No mode = full profile.
  const showRentalSection = forRent && urlMode !== 'buy';
  const showSaleSection = forSale && urlMode !== 'rent';

  // In-app booking is offered for rentals (not in buy-only view, not when sold)
  const canBook = showRentalSection && !sold;

  const heroStatus = sold
    ? { label: 'Sold', color: '#ef4444', pulse: false }
    : buyMode
      ? { label: 'For Sale', color: '#2563eb', pulse: true }
      : car.isAvailable
        ? { label: 'Available Now', color: '#16a34a', pulse: true }
        : { label: 'Currently Booked', color: '#ef4444', pulse: false };

  // CTA + WhatsApp wording adapts to buy vs rent
  const ctaLabel = sold ? 'Sold Out' : buyMode ? 'Call to Buy' : 'Call to Book';
  const waIntent = buyMode ? 'interested in buying' : 'interested in renting';
  const shareLink = urlMode ? `${shareUrl}?mode=${urlMode}` : shareUrl;
  const waHref = `https://wa.me/${waNumber(phone)}?text=${encodeURIComponent(`Hi ${agency}, I'm ${waIntent} your ${name}. Is it available?\n${shareLink}`)}`;

  // Used-car (buy) detail tiles
  const saleDetails = [
    car.variant && { icon: Award, label: 'Variant', value: car.variant },
    (sd.registrationYear || car.year) && { icon: Calendar, label: 'Reg. Year', value: sd.registrationYear || car.year },
    sd.kmDriven && { icon: Gauge, label: 'KM Driven', value: `${Number(sd.kmDriven).toLocaleString('en-IN')} km` },
    sd.noOfOwners && { icon: Users, label: 'Ownership', value: `${ordinal(sd.noOfOwners)} owner` },
    sd.condition && { icon: Award, label: 'Condition', value: tc(sd.condition) },
    car.fuelType && { icon: Fuel, label: 'Fuel', value: tc(car.fuelType) },
    car.transmission && { icon: Cog, label: 'Transmission', value: tc(car.transmission) },
    typeof sd.accidentFree === 'boolean' && { icon: ShieldCheck, label: 'Accident-free', value: sd.accidentFree ? 'Yes' : 'No' },
    typeof sd.rcAvailable === 'boolean' && { icon: FileText, label: 'RC', value: sd.rcAvailable ? 'Available' : 'No' },
    sd.insuranceValidTill && { icon: FileText, label: 'Insurance till', value: fmtDate(sd.insuranceValidTill) },
    // Only stated when the seller committed to an answer — '' stays off the page.
    SHARE_LOAN_LABELS[sd.loanStatus] && { icon: FileText, label: 'Loan', value: SHARE_LOAN_LABELS[sd.loanStatus] },
  ].filter(Boolean);

  return (
    <div className="min-h-screen" style={{ background: '#f4f6f9' }}>
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur border-b" style={{ background: 'rgba(255,255,255,.85)', borderColor: '#eef0f3' }}>
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            {logo ? <img src={logo} alt="" className="w-9 h-9 rounded-full object-cover ring-2 ring-white shadow" /> :
              <span className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold" style={{ background: NAVY }}>W</span>}
            <div className="leading-tight">
              <div className="text-sm font-bold" style={{ color: NAVY }}>{agency}</div>
              <div className="text-[11px] text-gray-400">Powered by Wheels4Rent</div>
            </div>
          </div>
          <button onClick={() => setShareOpen(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold shadow-sm"
            style={{ background: YELLOW, color: NAVY }}>
            <Share2 size={16} /> Share
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 pb-28 pt-5">
        {/* Hero */}
        <Reveal><Gallery images={car.images || []} status={heroStatus} location={car.location?.city} /></Reveal>

        {/* Title */}
        <Reveal delay={80}>
          <div className="mt-6">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide" style={{ background: '#EAF6FF', color: '#0369a1' }}>
                {tc(car.vehicleType || 'Vehicle')}
              </span>
              <span className="px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide flex items-center gap-1"
                style={{ background: sold ? '#fee2e2' : buyMode ? '#dbeafe' : '#fef9c3', color: sold ? '#b91c1c' : buyMode ? '#1d4ed8' : '#854d0e' }}>
                <Tag size={11} /> {purposeLabel}
              </span>
              {car.year && <span className="text-sm text-gray-400 font-medium">{car.year}</span>}
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold mt-2" style={{ color: NAVY }}>{name}</h1>
            {cityLine && (
              <div className="mt-3">
                <span className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-bold shadow-sm"
                  style={{ background: '#EAF6FF', color: '#0369a1' }}>
                  <MapPin size={16} /> Available in {cityLine}
                </span>
                {car.location?.address && (
                  <p className="text-sm text-gray-500 mt-2 flex items-start gap-1.5">
                    <MapPin size={14} className="mt-0.5 shrink-0" /> {car.location.address}
                  </p>
                )}
              </div>
            )}
          </div>
        </Reveal>

        {/* Specs */}
        {specs.length > 0 && (
          <Reveal delay={120}>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-6">
              {specs.map((s, idx) => <Spec key={idx} {...s} />)}
            </div>
          </Reveal>
        )}

        {/* Rental + Buy sections — ordered by the shared mode */}
        {(() => {
          const rentalBlock = showRentalSection && prices.length > 0 && (
            <Reveal delay={100} key="rent">
              <div className="mt-8">
                <h2 className="text-lg font-bold mb-3 flex items-center gap-2" style={{ color: NAVY }}><IndianRupee size={18} /> Rental Rates</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {prices.map((p, idx) => <PriceCard key={idx} {...p} />)}
                </div>
                {car.driverBataPerDay ? <p className="text-xs text-gray-400 mt-2">+ Driver bata {inr(car.driverBataPerDay)}/day for outstation trips</p> : null}
              </div>
            </Reveal>
          );

          const saleBlock = showSaleSection && (
            <Reveal delay={100} key="sale">
              <div className="mt-8">
                <h2 className="text-lg font-bold mb-3 flex items-center gap-2" style={{ color: NAVY }}><Tag size={18} /> For Sale</h2>
                <div className="rounded-2xl p-5 flex items-center justify-between text-white shadow-md" style={{ background: `linear-gradient(135deg, ${NAVY}, #16407a)` }}>
                  <div>
                    <div className="text-xs font-semibold" style={{ color: YELLOW }}>Sale Price</div>
                    <div className="text-3xl font-extrabold leading-none mt-1">{showSalePrice ? inr(car.salePrice) : 'Contact for Price'}</div>
                    <div className="text-sm text-white/70 mt-1">{saleTypeLabel}</div>
                  </div>
                  {sold && <span className="px-3 py-1.5 rounded-full text-sm font-extrabold" style={{ background: '#ef4444' }}>SOLD</span>}
                </div>
                {saleDetails.length > 0 && (
                  <div className="grid grid-cols-3 gap-3 mt-3">
                    {saleDetails.map((s, idx) => <Spec key={idx} {...s} />)}
                  </div>
                )}
                <ListingDisclaimer mode="sale" className="mt-4" />
              </div>
            </Reveal>
          );

          return leadBuy ? <>{saleBlock}{rentalBlock}</> : <>{rentalBlock}{saleBlock}</>;
        })()}

        {/* Features */}
        {(car.features || []).length > 0 && (
          <Reveal delay={100}>
            <div className="mt-8">
              <h2 className="text-lg font-bold mb-3 flex items-center gap-2" style={{ color: NAVY }}><Sparkles size={18} /> Features</h2>
              <div className="flex flex-wrap gap-2">
                {car.features.map((f, idx) => (
                  <span key={idx}
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white border border-gray-100 shadow-sm text-sm font-medium"
                    style={{ color: NAVY, animation: `chipIn .5s ease both`, animationDelay: `${idx * 45}ms` }}>
                    <CheckCircle2 size={15} style={{ color: '#16a34a' }} /> {f}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        )}

        {/* Description */}
        {car.description && (
          <Reveal delay={100}>
            <div className="mt-8 bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <h2 className="text-lg font-bold mb-2" style={{ color: NAVY }}>About this vehicle</h2>
              <p className="text-gray-600 leading-relaxed whitespace-pre-line">{car.description}</p>
            </div>
          </Reveal>
        )}

        {/* Agency card */}
        <Reveal delay={100}>
          <div className="mt-8 rounded-3xl p-6 text-white shadow-xl" style={{ background: `linear-gradient(135deg, ${NAVY}, #0d2547)` }}>
            <div className="flex items-center gap-3">
              {logo ? <img src={logo} alt="" className="w-14 h-14 rounded-2xl object-cover ring-2 ring-white/20" /> :
                <span className="w-14 h-14 rounded-2xl flex items-center justify-center font-bold text-xl" style={{ background: YELLOW, color: NAVY }}>{agency[0]}</span>}
              <div>
                <div className="flex items-center gap-1.5 font-bold text-lg">{agency}
                  <ShieldCheck size={16} style={{ color: YELLOW }} /></div>
                <div className="text-white/60 text-sm flex items-center gap-1"><MapPin size={13} /> {cityLine || 'Karnataka'}</div>
              </div>
            </div>
            <p className="text-white/70 text-sm mt-4">{sold ? 'This vehicle has been sold. Contact the owner for similar vehicles.' : buyMode ? 'Contact the owner directly to inspect this vehicle and finalise the purchase.' : 'Book instantly below, or reach the owner directly to check availability.'}</p>
            {canBook && (
              <button onClick={() => setBookOpen(true)}
                className="w-full mt-4 flex items-center justify-center gap-2 py-3.5 rounded-xl font-extrabold shadow-lg" style={{ background: YELLOW, color: NAVY }}>
                <CalendarCheck size={19} /> Book Now
              </button>
            )}
            <div className="grid grid-cols-2 gap-3 mt-3">
              <a href={`tel:${phone}`} className="flex items-center justify-center gap-2 py-3 rounded-xl font-bold border border-white/20 text-white" style={canBook ? { background: 'rgba(255,255,255,.08)' } : { background: YELLOW, color: NAVY, border: 'none' }}>
                <Phone size={18} /> {ctaLabel}
              </a>
              <a href={waHref} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-white" style={{ background: '#25D366' }}>
                <MessageCircle size={18} /> WhatsApp
              </a>
            </div>
          </div>
        </Reveal>

        {/* Owner CTA — invite travel agencies to list */}
        <Reveal delay={100}>
          <div className="mt-8 rounded-3xl p-6 bg-white border-2 border-dashed" style={{ borderColor: '#e2e6ec' }}>
            <div className="flex items-start gap-3">
              <span className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: NAVY }}>
                <Building2 size={20} style={{ color: YELLOW }} />
              </span>
              <div className="flex-1">
                <h3 className="font-bold" style={{ color: NAVY }}>Own vehicles for rent or sale?</h3>
                <p className="text-sm text-gray-500 mt-0.5">List your travel agency on Wheels4Rent and get your own shareable vehicle pages like this one — free to start.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <Link to="/register" className="flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold" style={{ background: YELLOW, color: NAVY }}>
                <Building2 size={16} /> Create your agency
              </Link>
              <Link to="/login" className="flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold border" style={{ borderColor: NAVY, color: NAVY }}>
                Owner login <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </Reveal>

        <div className="text-center mt-8 text-gray-400 text-sm">
          Listed on <span className="font-bold" style={{ color: NAVY }}>Wheels<span style={{ color: YELLOW, WebkitTextStroke: `0.5px ${NAVY}` }}>4</span>Rent</span> · Karnataka's Wheels Marketplace
        </div>
      </main>

      {/* Sticky mobile CTA */}
      <div className="fixed bottom-0 inset-x-0 z-40 sm:hidden border-t bg-white/95 backdrop-blur px-4 py-3" style={{ borderColor: '#eef0f3' }}>
        <div className="flex gap-2">
          {canBook ? (
            <button onClick={() => setBookOpen(true)} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold" style={{ background: YELLOW, color: NAVY }}><CalendarCheck size={18} /> Book Now</button>
          ) : (
            <a href={`tel:${phone}`} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold" style={{ background: YELLOW, color: NAVY }}><Phone size={18} /> {sold ? 'Call' : buyMode ? 'Call to Buy' : 'Call'}</a>
          )}
          <a href={waHref} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-white" style={{ background: '#25D366' }}><MessageCircle size={18} /> WhatsApp</a>
          <button onClick={() => setShareOpen(true)} className="w-12 flex items-center justify-center rounded-xl border" style={{ borderColor: '#e5e7eb', color: NAVY }}><Share2 size={18} /></button>
        </div>
      </div>

      <BookVehicleModal open={bookOpen} onClose={() => setBookOpen(false)} car={car} />

      <ShareSheet open={shareOpen} onClose={() => setShareOpen(false)} url={shareLink}
        title={`${name} — ${agency}`}
        text={buyMode
          ? (showSalePrice ? `For sale · ${inr(car.salePrice)}` : 'Available for sale')
          : (primaryPrice ? `${inr(primaryPrice.amount)} ${primaryPrice.unit}` : 'Available for rent')} />

      <style>{`@keyframes chipIn{from{opacity:0;transform:translateY(10px) scale(.96)}to{opacity:1;transform:none}}`}</style>
    </div>
  );
}
